/**
 * CALL FORM MODAL — Modale di registrazione chiamata.
 *
 * Aperto da PersonDetail/BusinessDetail (azioni "+ Nuova chiamata" o
 * dalla timeline tab 'chiamata').
 *
 * Campi (schema yaml `CallInput`):
 *  - direction (IN/OUT, required)
 *  - callDate (datetime, required) — di default "now"
 *  - description (string, required)
 *  - status (CallStatus enum, required)
 *
 * Al submit:
 *  1. dispatch createCall(payload) -> Call
 *  2. createBidirectionalRelationship Call<->record per ogni associato
 *  3. se linkedEntity presente (aperto da Detail), anche quello collegato
 */

import { useState, useRef, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import CloseIcon from '@mui/icons-material/Close';
import { datetimeLocalToIso, nowDatetimeLocal, htmlHasText } from '@crm/shared';
import { RichTextEditor } from '../../components/RichTextEditor';
import { useToast } from '../../components/useToast';
import { useAppDispatch } from '../../store/hooks';
import { createCall } from './callThunks';
import { getEntityFqn } from '../../entityRegistry/entityRegistry';
import { useAssociatedRecordsBinder } from '../../components/useAssociatedRecordsBinder';
import { AssociatedRecordsField } from '../../components/AssociatedRecordsField';
import { CALL_DIRECTIONS, CALL_STATUSES, type Call, type CallCreatePayload, type CallDirection, type CallStatus } from './callTypes';

interface CallFormModalProps {
  onClose: () => void;
  onCreated?: () => void;
  /** Direzione di default quando il modal viene aperto da un'azione specifica
   *  (es. bottone "Phone" potrebbe pre-impostare OUT). */
  initialDirection?: CallDirection;
  /** Se presente, dopo la create dispatcha una Relationship bidirezionale
   *  Call<->linkedEntity (es. Person/Business corrente). */
  linkedEntity?: { className: string; classPK: number };
}

const FORM_ID = 'call-form';

function defaultForm(initialDirection?: CallDirection): CallCreatePayload {
  return {
    direction: initialDirection ?? 'OUT',
    callDate: datetimeLocalToIso(nowDatetimeLocal()) ?? '',
    description: '',
    status: 'connected',
  };
}

export default function CallFormModal({ onClose, onCreated, initialDirection, linkedEntity }: CallFormModalProps) {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const toast = useToast();

  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<CallCreatePayload>(() => defaultForm(initialDirection));
  // Stato datetime-local separato (formato "YYYY-MM-DDTHH:mm"); al submit
  // viene convertito in ISO "...:00Z" coerente con callDate atteso.
  const [callDateLocal, setCallDateLocal] = useState<string>(() => nowDatetimeLocal());
  // Rimonta il RichTextEditor (non controllato) per svuotarlo dopo un submit "keep".
  const [editorKey, setEditorKey] = useState(0);

  const callFqn = getEntityFqn('Call');

  const { records, setRecords, applyAtSubmit, pickerProps } = useAssociatedRecordsBinder({
    mode: 'create',
    entityFqn: callFqn ?? undefined,
    reference: linkedEntity ?? null,
  });

  const submitIntentRef = useRef<'close' | 'keep'>('close');

  const canSubmit = htmlHasText(form.description) && callDateLocal.length > 0;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    const intent = submitIntentRef.current;
    submitIntentRef.current = 'close';

    const payload: CallCreatePayload = {
      ...form,
      callDate: datetimeLocalToIso(callDateLocal) ?? '',
    };

    setSubmitting(true);
    try {
      let created: Call;
      try {
        created = (await dispatch(createCall(payload)).unwrap()) as Call;
      } catch (err) {
        toast({ severity: 'error', message: err instanceof Error ? err.message : String(err) });
        return;
      }
      if (!callFqn) {
        console.warn(
          '[relationship] CallFormModal: Call non registrato nel registry — link saltati',
          { selectedRecords: records.length },
        );
      } else {
        await applyAtSubmit?.(callFqn, created.callId);
      }
      toast({ severity: 'success', message: t('common.saved') });
      onCreated?.();
      if (intent === 'keep') {
        setForm(defaultForm(initialDirection));
        setCallDateLocal(nowDatetimeLocal());
        setEditorKey((k) => k + 1);
        setRecords?.([]);
      } else {
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  }

  const footer = (
    <>
      <Button
        type="submit"
        form={FORM_ID}
        onClick={() => { submitIntentRef.current = 'close'; }}
        variant="contained"
        disabled={submitting || !canSubmit}
      >
        {t('common.create')}
      </Button>
      <Button type="button" onClick={onClose} variant="outlined">
        {t('common.cancel')}
      </Button>
    </>
  );

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth className="app-dialog">
      <DialogTitle className="app-dialog__header">
        <span className="app-dialog__title">{t('call.createTitle')}</span>
        <IconButton
          aria-label={t('common.cancel')}
          onClick={onClose}
          size="small"
          className="app-dialog__close"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent className="app-dialog__body">
        <form id={FORM_ID} onSubmit={handleSubmit} className="call-form">
          {/* Riga 1: Direzione | Esito (2 col, allineato al layout grid del Task) */}
          <div className="task-form__grid task-form__grid--2col">
            <TextField
              select
              label={t('call.direction')}
              value={form.direction}
              onChange={(e) => setForm({ ...form, direction: e.target.value as CallDirection })}
              required
              fullWidth
              className="form-field"
            >
              {CALL_DIRECTIONS.map((d) => (
                <MenuItem key={d.value} value={d.value}>{t(d.label)}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t('call.status')}
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as CallStatus })}
              required
              fullWidth
              className="form-field"
            >
              {CALL_STATUSES.map((s) => (
                <MenuItem key={s.value} value={s.value}>{t(s.label)}</MenuItem>
              ))}
            </TextField>
          </div>

          {/* Data/ora */}
          <TextField
            type="datetime-local"
            label={t('call.callDate')}
            value={callDateLocal}
            onChange={(e) => setCallDateLocal(e.target.value)}
            required
            fullWidth
            className="form-field"
            slotProps={{ inputLabel: { shrink: true } }}
          />

          {/* Descrizione full-width WYSIWYG */}
          <div className="form-field">
            <label className="field-row__label text-xs text-muted">{t('call.description')}</label>
            <RichTextEditor
              key={editorKey}
              onChange={(html) => setForm({ ...form, description: html })}
              placeholder={t('call.descriptionPlaceholder')}
            />
          </div>

          {/* Associa ai record (polimorfico) */}
          <AssociatedRecordsField
            {...pickerProps}
            records={records}
            onChange={(val) => setRecords?.(val)}
            displayMode="click-to-edit"
          />
        </form>
      </DialogContent>
      <DialogActions className="app-dialog__footer">{footer}</DialogActions>
    </Dialog>
  );
}
